import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal, viewChild } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IWebConfig } from 'src/app/shared/model/IWebConfig';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ApplicationError } from '../../services/application-errors';
import { CarouselComponent } from '../../components/carousel/carousel.component';
import { WebConfigService } from '../../services/web-config/web-config.service';
import { AuthStateService } from '../../services/auth/auth-state.service';
import { AppRoutes } from '../../routes/common.routes';
import { LoginIdentityComponent } from './login-identity/login-identity.component';
import { LoginIdentityChange } from './login-identity/login-identity.model';
import { PinLoginComponent } from './pin-login/pin-login.component';
import { PasswordLoginComponent } from './password-login/password-login.component';
import { LoginService } from './login.service';

@Component({
  selector: 'app-login',
  imports: [
    RouterLink,
    TranslatePipe,
    CarouselComponent,
    LoginIdentityComponent,
    PinLoginComponent,
    PasswordLoginComponent,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  animations: [
    trigger('flyInOut', [
      state('in', style({ transform: 'translateX(0)' })),
      transition('void => *', [style({ transform: 'translateX(100%)' }), animate('300ms ease-out')]),
      transition('* => void', [animate('300ms ease-out', style({ transform: 'translateX(-100%)' }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class LoginComponent implements OnInit {
  #authState = inject(AuthStateService);
  #translate = inject(TranslateService);
  #logger = inject(NGXLogger);
  #loginService = inject(LoginService);
  private webconfig: IWebConfig = inject(WebConfigService);

  public authService = inject(AuthService);
  public router = inject(Router);

  public logo = this.webconfig.assets.logo;
  public logoWhite = this.webconfig.assets.logoWhite;
  public orgId = this.webconfig.orgId;
  public loginType = this.webconfig.loginType.toLocaleLowerCase();
  public loginMethod = this.webconfig.loginMethod;
  public accountType = this.webconfig.accountType;
  public portalId = this.webconfig.portalId;
  public portal = this.webconfig.portal;
  public isAllowedToCreateAccount = this.webconfig.isAllowedToCreateAccount;
  public link = { forgotPassword: '/auth/forgot-password', register: '/auth/register' };

  public incorrectPassword = signal(false);
  public errors = signal<Set<string>>(new Set());
  public identity = signal<LoginIdentityChange>({ active: 1, email: '', mobile: null, valid: false });

  pinStep = viewChild(PinLoginComponent);
  passwordStep = viewChild(PasswordLoginComponent);

  ngOnInit(): void {
    this.authService.removeLoginCredentials();
  }

  public clearErrors(): void {
    this.errors.set(new Set());
  }

  public async onCredentialSubmit(password: string): Promise<void> {
    this.errors.set(new Set());
    const { active, email, mobile, valid } = this.identity();

    if (!valid) {
      this.#logger.error(this.#translate.instant('MESSAGES.ERRORS.INVALID_LOGIN_FORM'));
      return;
    }

    try {
      sessionStorage.removeItem('access_token');
      const userDetails = await this.#loginService.resolveUserIdentity(active, email, mobile);
      const { valid: identityValid, errorMessage } = this.#loginService.validateUserIdentity(userDetails, active);

      if (!identityValid) {
        if (errorMessage) this.errors.update((current) => new Set(current).add(errorMessage));
        return;
      }

      await this.authService.login(userDetails.username, password);
      const authUser = this.#authState.user();

      if (authUser) {
        this.router.navigate([AppRoutes.dashboard]);
      }
    } catch (e) {
      if (e instanceof ApplicationError) {
        this.errors.update((current) => new Set(current).add(e.details));
      } else {
        this.incorrectPassword.set(true);
      }
      this.#logger.error(this.#translate.instant('MESSAGES.ERRORS.FAILED_TO_LOGIN'), e.message);
      this.resetPasswordInput();
    }
  }

  resetPasswordInput(): void {
    this.pinStep()?.reset();
    this.passwordStep()?.reset();
  }
}
